import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { CarbonMark } from './CarbonMark.js'

/**
 * The frame every unauthenticated screen sits in.
 *
 * Two columns: the form, and a panel that says what the product is for. The
 * panel is the only marketing in the application and it is the first thing a
 * new person sees, so it makes the same claim the product does — that this tool
 * shows you the sends nobody else will admit to.
 *
 * It disappears below 900px rather than stacking. Stacking would put a
 * paragraph of persuasion above the form on a phone, between someone and the
 * thing they came to do.
 *
 * Deliberately not the application shell. Nobody here is signed in, and a
 * product navigation would offer them places they cannot go.
 */
export function AuthLayout({ children }: { readonly children: ReactNode }) {
  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="flex flex-col items-center justify-center px-8 py-12">
        <div className="w-full max-w-[404px]">
          <div className="mb-9 flex items-center gap-2.5">
            <CarbonMark className="h-[30px] w-[26px]" />
            <span className="font-bold text-[17px] text-t1 -tracking-[0.01em]">Carbon Mail</span>
          </div>
          {children}

          {/*
            Beneath every unauthenticated screen rather than on the sign-up
            form alone, because the pages have two audiences and only one of
            them is signing up: a recipient who followed an unsubscribe link
            wants to know who holds their address, and they arrive here, not
            at a form.
          */}
          <nav className="mt-10 flex justify-center gap-5 text-[12px]">
            <Link className="font-medium text-t3 hover:text-t1" to="/privacy">
              Privacy
            </Link>
            <Link className="font-medium text-t3 hover:text-t1" to="/terms">
              Terms
            </Link>
          </nav>
        </div>
      </div>

      <aside className="hidden items-center justify-center bg-linear-145 from-[oklch(0.25_0.03_235)] via-[oklch(0.20_0.02_250)] to-[oklch(0.24_0.05_205)] p-12 lg:flex">
        <div className="max-w-[420px] text-white">
          <p className="mb-3.5 font-semibold text-[11px] text-brand-strong uppercase tracking-[0.12em]">
            Why teams move
          </p>
          <p className="mb-7 font-semibold text-[26px] leading-[1.35] -tracking-[0.01em]">
            Most tools show a green tick. Carbon Mail shows the sends that came back{' '}
            <span className="text-brand-strong">unknown</span> — and what to do about them.
          </p>
          <ul className="grid gap-3">
            <Claim>Suppression reasons kept apart — unsubscribe, bounce, complaint, manual.</Claim>
            <Claim>Bounce and complaint rates against the thresholds that suspend accounts.</Claim>
            <Claim>An append-only consent record you can hand to a regulator.</Claim>
          </ul>
        </div>
      </aside>
    </div>
  )
}

function Claim({ children }: { readonly children: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        className="mt-0.5 h-[18px] w-[18px] flex-none text-brand-strong"
        aria-hidden="true"
      >
        <path d="M5 13l4 4L19 7" />
      </svg>
      <span className="text-[14px] text-white/80">{children}</span>
    </li>
  )
}
